import React, { useState, forwardRef, useImperativeHandle, useContext } from 'react';
import { Drawer, Form, Space, Button, Input, message, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import { debounce } from 'lodash';

import IntlContext from '@/components/Intl/IntlContext';
import * as ReverseApi from '@/api/bill/reverse';
import { parseFen2Currency, parse2Number, parseYuan2Fen, parseFen2Yuan } from '@/utils/number';
import { renderIntlLabel } from '@/utils';
import {
  validReverseRemark,
  ReverseResonCodeQT,
  validDetailNotRequire,
  validDetailRequire,
} from '@/pages/reservation/roomExchange/helper';

import ReserveService from '@/api/reserve';
import { validateAmount, formatAmount } from '../util/helper';
import { OpenReverseDrawerProps, ReverseDrawerProps } from '../interface';
import commonStyles from '../common.module.scss';
import ReverseInfo from './info';
import styles from './index.module.scss';

const { TextArea } = Input;
const { Option } = Select;

const Reverse: React.FC<ReverseDrawerProps> = forwardRef((props, ref) => {
  const [reverseForm] = Form.useForm();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [amount, setAmount] = useState(0); // 可冲调金额
  const [bill, setBill] = useState(null);
  const [reverseAmount, setReverseAmount] = useState('');
  const [reverseRemark, setReverseRemark] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const intlCtx = useContext(IntlContext);
  const [reverseReason, setReverseReason] = useState('');

  const [reasons, setReasons] = useState<any[]>([]);
  const [resonCode, setResonCode] = useState(null);

  // 获取原因
  const getReasonList = async () => {
    let result: any = [];
    try {
      const res = await ReserveService.getDictListByType({
        code: 'REVERSE_REASON',
      });

      result =
        (res.data?.list || []).map((item) => {
          return {
            value: item?.dictKey,
            label: item?.dictValue,
          };
        }) || [];
    } catch (_) {
      result = [];
    }
    setReasons(result);
  };

  // 获取可冲调金额
  const fetchCanReverseAmount = async (no) => {
    try {
      const res = await ReverseApi.fetchReverseAmount({
        no,
      });
      if (res && res.status) {
        setAmount(res.data);
      }
    } catch (err) {
      setAmount(0);
    }
  };

  const openDrawer = (params: OpenReverseDrawerProps) => {
    setBill(params.bill);
    fetchCanReverseAmount(params.bill.no);
    setDrawerVisible(true);
    getReasonList();
  };

  useImperativeHandle(ref, () => ({
    openDrawer,
  }));

  // reset states after drawer closed
  const changeVisible = (visible) => {
    if (!visible) {
      setAmount(null);
      setBill(null);
      setReverseAmount('');
      setReverseRemark('');
      setReverseReason('');
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  // 冲调提交
  const submit = debounce(async () => {
    let values;
    try {
      values = await reverseForm.validateFields();
    } catch (err) {
      message.warning(intl.get('bill_reverse_input_amount_and_remark'));
    }

    try {
      setSubmitLoading(true);

      const params = {
        billItemNo: bill.no, // 冲调账单号
        amount: parse2Number(parseYuan2Fen(values.reverseAmount)), // 冲减金额
        remark: `${reverseReason}${values.remark ? '，' : ''}${values.remark || ''}`, // 备注
        reverseDetail: values.remark,
        reverseReason,
        reverseReasonCode: values.reverseReason,
        resvNo: bill.resvNo,
      };
      const res: any = await ReverseApi.confirmReverse(params);

      if (res && res.status) {
        message.success(intl.get('bill_reverse_operate_success'));
        closeDrawer();

        // callback parent component
        props.reverseCallback();
      } else {
        message.success(res.msg || intl.get('bill_reverse_operate_fail'));
      }

      setSubmitLoading(false);
    } catch (err) {
      setSubmitLoading(false);
    }
  }, 200);

  const drawerFooter = () => {
    let disabled = reverseAmount === '' || +reverseAmount <= 0;
    if (resonCode !== ReverseResonCodeQT) {
      disabled = disabled || validDetailNotRequire(reverseRemark);
    } else {
      disabled = disabled || validDetailRequire(reverseRemark);
    }

    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        <Button type="primary" disabled={disabled} loading={submitLoading} onClick={() => submit()}>
          {intl.get('bill_reverse_confirm_btn')}
        </Button>
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  const reasonChange = (e) => {
    const res = reasons.find((item) => e === item.value);
    if (res) {
      setReverseReason(res?.label);
      setResonCode(res?.value);
    }
  };

  return (
    <Drawer
      width="50%"
      title={intl.get('bill_config_chong_tiao')}
      open={drawerVisible}
      maskClosable={false}
      closable={false}
      destroyOnClose
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <ReverseInfo info={bill} />
      <dl className={styles['reverse-amount']}>
        <dt className={commonStyles['bill-block-title']}>{intl.get('bill_config_chong_tiao')}</dt>
        <dd>
          <Form
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 8 }}
            form={reverseForm}
            preserve={false}
            className={styles['reverse-form']}
          >
            <Form.Item label={intl.get('bill_reverse_amount')} name="amount">
              {parseFen2Currency(amount)}
            </Form.Item>
            <Form.Item
              label={intl.get('bill_reverse_amount_label')}
              name="reverseAmount"
              initialValue={reverseAmount}
              rules={[
                {
                  validator: (_, value) => {
                    return validateAmount(value, {
                      required: true,
                    });
                  },
                },
              ]}
            >
              <Input
                allowClear
                prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                placeholder={intl.get('bill_reverse_amount_input')}
                onChange={(e) => {
                  setReverseAmount(e.target.value);
                }}
                onBlur={(e) => {
                  const v = formatAmount(e.target.value, {
                    max: parse2Number(parseFen2Yuan(amount)),
                  });
                  reverseForm.setFieldsValue({
                    reverseAmount: v,
                  });
                }}
              />
            </Form.Item>
            <Form.Item
              name="reverseReason"
              label={renderIntlLabel('bill_reverse_brew_reason')}
              rules={[
                {
                  required: true,
                  message: `${renderIntlLabel('bill_credit_please_select')}${renderIntlLabel(
                    'bill_reverse_brew_reason',
                  )}`,
                },
              ]}
            >
              <Select
                style={{ width: '240px' }}
                placeholder={renderIntlLabel('bill_credit_please_select')}
                onChange={reasonChange}
              >
                {reasons.map((item) => (
                  <Option value={item.value} label={item.label} key={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label={renderIntlLabel('bill_reverse_brew_remark')}
              name="remark"
              initialValue={reverseRemark}
              rules={[
                {
                  validator: (_, value) => validReverseRemark(_, value, resonCode),
                },
              ]}
            >
              <TextArea
                showCount
                rows={4}
                placeholder={
                  resonCode !== ReverseResonCodeQT
                    ? intl.get('drawer_exchange_remark_exchange_detail_placeholder_choose_input')
                    : intl.get('drawer_exchange_remark_exchange_detail_placeholder')
                }
                maxLength={10000}
                onChange={(e) => {
                  setReverseRemark(e.target.value);
                }}
              />
            </Form.Item>
          </Form>
        </dd>
      </dl>
    </Drawer>
  );
});

export default Reverse;
